import type { PetSpecies, PetState } from '../pet';
import { getLocalDate } from '../../utils/time';
import { duration } from '../../utils/duration';
import TextMessage from '../../airtable/TextMessage';
import { assertNever } from '../../utils';

export type PetStatusNotification =
  | 'sick'
  | 'tooth_decay'
  | 'scared'
  | 'evolving'
  | 'very_dirty'
  | 'very_hungry'
  | 'very_lonely'
  | 'very_pee'
  | 'dirty'
  | 'hungry'
  | 'lonely'
  | 'pee'
  | 'dead'
  | 'dying';

export type PetMessage<TSpecies extends PetSpecies = PetSpecies> = Extract<
  typeof TextMessage[number],
  { pet_id: TSpecies }
>;

export type PetChatTime = PetMessage['time'];

function isPetStatusNotification(
  status: PetState,
): status is PetStatusNotification {
  switch (status) {
    case 'dead':
    case 'evolving':
    case 'dying':
    case 'dirty':
    case 'hungry':
    case 'lonely':
    case 'pee':
    case 'scared':
    case 'sick':
    case 'tooth_decay':
    case 'very_dirty':
    case 'very_hungry':
    case 'very_lonely':
    case 'very_pee':
      return true;

    case 'had_visitor':
    case 'happy':
    case 'normal':
    case 'sad':
    case 'sleeping':
    case 'very_sad':
    case 'want_to_go_out':
      return false;

    default:
      assertNever(status);
  }
}

export function canSendPetStatusNotification(
  receiveMessagesSetting: 'on' | 'less' | 'off',
  status: PetState,
): status is PetStatusNotification {
  if (receiveMessagesSetting === 'off') {
    // Notifications disabled, bail.
    return false;
  }

  if (!isPetStatusNotification(status)) {
    // No notification for this status
    return false;
  }

  // Do not send all status messages for 'less' setting.
  if (
    ['dirty', 'hungry', 'lonely', 'pee'].includes(status) &&
    receiveMessagesSetting === 'less'
  ) {
    return false;
  }

  return true;
}

export const petMessages = TextMessage.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {} as Record<PetMessage['id'], PetMessage>);

export const petSpeciesMessages = TextMessage.reduce((acc, item) => {
  const species = item.pet_id;
  if (!acc[species]) {
    acc[species] = [];
  }
  const messages = acc[species] as PetMessage[];
  messages.push(item);
  return acc;
}, {} as { [Species in PetSpecies]: PetMessage<Species>[] });

const petChatScheduleHours = {
  morning: 7,
  lunch: 12,
  evening: 20,
};

export function getNextChatMessageSchedule(
  now: number,
  random: number,
  timeZone: string,
) {
  const date = getLocalDate(now, timeZone);
  const jstNow = date.getTime();
  const hours = date.getHours();
  date.setMinutes(0, 0, 0);
  const startOfThisHour = date.getTime();
  const partOfHour = jstNow - startOfThisHour;

  let period: PetChatTime;
  if (hours >= petChatScheduleHours.evening) {
    period = 'morning';
  } else if (hours >= petChatScheduleHours.lunch) {
    period = 'evening';
  } else {
    period = 'lunch';
  }

  let startHours = petChatScheduleHours[period] - hours;
  if (period === 'morning') {
    startHours += 24;
  }
  let delayInMS = duration({ hours: startHours }) - partOfHour;
  delayInMS += random * duration({ hours: 1 });

  return { period, delayInMS };
}
